<template>
  <div class="input def-input">

    <div class="input-wrapper">
      <input
        v-bind="inputAttrs"
        :label="label"
        :id="inputId"
        :type="computedType"
        :value="localValue"
        :placeholder="floatingPlaceholder"
        :aria-required="required ? 'true' : null"
        :aria-invalid="hasError ? 'true' : null"
        :aria-describedby="describedBy"
        class="input-field"
        :class="{ 'has-password-toggle': type === 'password' && showPasswordEye }"
        @input="onInput"
        @focus="isFocused = true"
        @blur="isFocused = false"
        :autocomplete="inputAutocomplete"
        :min="type === 'date' ? min : undefined"
        :max="type === 'date' ? max : undefined"
        :maxlength="maxlength"
      />
      <label
        :id="labelId"
        :for="inputId"
        class="input-label input-label-control"
        :class="{ floating: isFocused || hasValue, error: hasError }"
      >
        {{ label }}<span v-if="required" role="presentation" aria-hidden="true"> *</span>
      </label>
      <button
        v-if="type === 'password' && showPasswordEye"
        type="button"
        class="toggle-password"
        @click="togglePasswordVisibility"
        :title="passwordAriaLabelState"
        :aria-label="passwordAriaLabelState"
        :aria-pressed="showPassword ? 'true' : 'false'"
        :disabled="isDisabled"
      >
        <font-awesome-icon :icon="showPassword ? ['fas', 'eye-slash'] : ['fas', 'eye']" />
      </button>
    </div>
    <div v-if="error" :id="errorId" class="input-error" role="alert" aria-live="assertive">
      {{ error }}
    </div>
    <div v-if="inputError" :id="inputErrorId" class="input-error" role="alert" aria-live="assertive">
      {{ inputError }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'Input',
  inheritAttrs: false,
  props: {
    value: [String, Number],
    label: { type: String, required: true },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    error: { type: String, default: '' },
    required: { type: Boolean, default: false },
    id: { type: String, default: null },
    min: { type: [String, Number], default: '' }, // for date/currency type
    max: { type: [String, Number], default: '' }, // for date/currency type
    step: { type: [String, Number], default: 1 }, // for range slider
    allowedValues: { type: Array, default: () => [] }, // allowed currency values passed from parent
    maxlength: { type: Number, default: undefined },
  },
  data() {
    return {
      showPassword: false,
      isFocused: false,
      localValue: this.normalizeValue(this.value),
      inputError: '',
    };
  },
  watch: {
    value(newVal) {
      this.localValue = this.normalizeValue(newVal);
    }
  },
  computed: {
    showPasswordEye() {
      return window.config.SHOW_PASSWORD_VISIBILITY_SWITCH;
    },
    inputId() {
      return this.id || `input-${this._uid}`;
    },
    labelId() {
      return `${this.inputId}-label`;
    },
    errorId() {
      return `${this.inputId}-error`;
    },
    inputErrorId() {
      return `${this.inputId}-internal-error`;
    },
    describedBy() {
      const ids = [];
      if (this.$attrs['aria-describedby']) {
        ids.push(this.$attrs['aria-describedby']);
      }
      if (this.error) {
        ids.push(this.errorId);
      }
      if (this.inputError) {
        ids.push(this.inputErrorId);
      }
      return ids.join(' ') || null;
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      delete attrs['aria-label'];
      delete attrs['aria-describedby'];
      delete attrs['aria-invalid'];
      delete attrs.autocomplete;
      return attrs;
    },
    computedType() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password';
      }
      return this.type;
    },
    inputAutocomplete() {
      return this.$attrs.autocomplete !== undefined ? this.$attrs.autocomplete : (this.type === 'date' ? 'off' : 'on');
    },
    isDisabled() {
      return this.$attrs.disabled === '' || this.$attrs.disabled === true || this.$attrs.disabled === 'true';
    },
    hasError() {
      return Boolean(this.error || this.inputError);
    },
    hasValue() {
      return this.localValue !== undefined && this.localValue !== null && this.localValue !== '';
    },
    floatingPlaceholder() {
      return (this.isFocused || this.hasValue) ? this.placeholder : '';
    },
    passwordAriaLabelState() {
      if (this.id === 'passwordRepeat') {
        return this.showPassword ? this.$t('ariaLabel.hidePasswordRepeat') : this.$t('ariaLabel.showPasswordRepeat');
      }
      return this.showPassword ? this.$t('ariaLabel.hidePassword') : this.$t('ariaLabel.showPassword');
    },
  },
  methods: {
    normalizeValue(value) {
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }

      if (this.allowedValues.length > 0) {
        return this.allowedValues[0];
      }

      return this.min;
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    onInput(event) {
      this.localValue = event.target.value;
      this.$emit('input', this.localValue);
    }
  },
};
</script>

<style scoped>
.input {
  border: 0;
  margin: 0;
  display: flex;
  padding: 0;
  position: relative;
  min-width: 0;
  flex-direction: column;
  vertical-align: top;
}
.input-label {
  text-align: left;
  pointer-events: none;
  transition: 0.2s cubic-bezier(0.4,0,0.2,1);
  background: transparent;
}
.input-label-control {
  top: 0;
  position: absolute;
  transform: translateY(24px) scale(1);
  z-index: 2;
}
.input-label-control.floating {
  transform: translateY(4px) scale(0.85);
  background: none;
}
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.input-field {
  text-align: left;
  width: 100%;
  padding: 24px 0 8px 0.75rem;
  outline: none;
  transition: background-color 9999s ease-in-out 0s;
}
.input-field.has-password-toggle {
  padding-right: 2.5rem;
}
.input-field:-webkit-autofill,
.input-field:-webkit-autofill:focus,
.input-field:-webkit-autofill:hover,
.input-field:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px transparent inset !important;
  box-shadow: 0 0 0 1000px transparent inset !important;
  transition: background-color 9999s ease-in-out 0s;
  background-color: transparent !important;
}
.input-field:-webkit-autofill ~ .input-label-control,
.input-field:autofill ~ .input-label-control {
  transform: translateY(4px) scale(0.85);
  background: none;
}
.toggle-password {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  height: 100%;
  z-index: 3;
  pointer-events: auto;
}
.input-error {
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5em;
  text-align: left;
}
</style>
